import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreateInvoiceData, InvoiceType, BrokerageQueueLead } from '@/types/invoice.types';
import { invoicesService } from '@/services/invoices.service';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { InvoiceCalculator } from '@/components/billing';

const invoiceTypes: { value: InvoiceType; label: string; description: string }[] = [
  {
    value: 'DEVELOPER',
    label: 'Developer',
    description: 'Invoice to the property developer',
  },
  {
    value: 'INDEPENDENT',
    label: 'Independent',
    description: 'Direct invoice to buyer/seller',
  },
  {
    value: 'BROKER_REFERRAL',
    label: 'Broker Referral',
    description: 'Invoice for broker referral commission',
  },
];

/**
 * Format amount in Indian currency format (lakhs/crores)
 */
const formatIndianCurrency = (amount: number): string => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
};

export const CreateInvoice: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedLeadId = searchParams.get('lead_id');

  const [brokerageQueue, setBrokerageQueue] = useState<BrokerageQueueLead[]>([]);
  const [isLoadingQueue, setIsLoadingQueue] = useState(true);

  const [formData, setFormData] = useState<CreateInvoiceData>({
    lead_id: preselectedLeadId || '',
    type: 'DEVELOPER',
    bill_to_name: '',
    bill_to_gstin: '',
    booking_value: 0,
    brokerage_percent: 2,
    gst_percent: 18,
    tds_percent: 1,
  });

  const [showGst, setShowGst] = useState(true);
  const [showTds, setShowTds] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBrokerageQueue = useCallback(async () => {
    try {
      setIsLoadingQueue(true);
      const response = await invoicesService.getBrokerageQueue();
      // API interceptor unwraps { success, data } - backend returns { leads: [...] }
      const queueData = response.data?.leads || [];
      setBrokerageQueue(queueData);

      // If we have a preselected lead, auto-fill booking value
      if (preselectedLeadId) {
        const selectedLead = queueData.find((l) => l.id === preselectedLeadId);
        if (selectedLead?.booking_value) {
          setFormData((prev) => ({
            ...prev,
            booking_value: selectedLead.booking_value || 0,
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching brokerage queue:', err);
    } finally {
      setIsLoadingQueue(false);
    }
  }, [preselectedLeadId]);

  useEffect(() => {
    fetchBrokerageQueue();
  }, [fetchBrokerageQueue]);

  const handleLeadChange = (leadId: string) => {
    const selectedLead = brokerageQueue.find((l) => l.id === leadId);
    setFormData({
      ...formData,
      lead_id: leadId,
      booking_value: selectedLead?.booking_value || 0,
    });
  };

  const handleInputChange = (field: keyof CreateInvoiceData, value: string | number) => {
    setFormData({ ...formData, [field]: value });
  };

  const validateForm = (): string | null => {
    if (!formData.lead_id) return 'Please select a lead';
    if (!formData.bill_to_name.trim()) return 'Bill to name is required';
    if (!formData.booking_value || formData.booking_value <= 0) return 'Please enter a valid booking value';
    if (!formData.brokerage_percent || formData.brokerage_percent <= 0) return 'Please enter brokerage percentage';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSubmitting(true);
      const submitData: CreateInvoiceData = {
        ...formData,
        gst_percent: showGst ? formData.gst_percent : 0,
        tds_percent: showTds ? formData.tds_percent : 0,
      };
      const response = await invoicesService.create(submitData);
      navigate(`/billing/invoices/${response.data.id}`);
    } catch (err) {
      setError('Failed to create invoice. Please try again.');
      console.error('Error creating invoice:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedLead = brokerageQueue.find((l) => l.id === formData.lead_id);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/billing')}
          className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
        >
          <svg
            className="w-5 h-5 text-secondary-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Create Invoice</h1>
          <p className="text-secondary-600 mt-1">
            Generate a new brokerage invoice
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Lead Selection */}
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Select Lead
              </h3>
              {isLoadingQueue ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-10 bg-secondary-200 rounded"></div>
                  <div className="h-10 bg-secondary-200 rounded"></div>
                </div>
              ) : brokerageQueue.length === 0 ? (
                <div className="text-center py-6 bg-secondary-50 rounded-lg">
                  <p className="text-secondary-600">
                    No leads available for invoicing.
                  </p>
                  <p className="text-sm text-secondary-500 mt-1">
                    Leads must be in "Booked" status to create an invoice.
                  </p>
                </div>
              ) : (
                <div>
                  <select
                    value={formData.lead_id}
                    onChange={(e) => handleLeadChange(e.target.value)}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select a lead...</option>
                    {brokerageQueue.map((lead) => (
                      <option key={lead.id} value={lead.id}>
                        {lead.name} (#{lead.lead_number})
                        {lead.booking_value && ` - ${formatIndianCurrency(lead.booking_value)}`}
                      </option>
                    ))}
                  </select>
                  {selectedLead && (
                    <div className="mt-3 p-3 bg-secondary-50 rounded-lg">
                      <p className="font-medium text-secondary-900">{selectedLead.name}</p>
                      <p className="text-sm text-secondary-600">#{selectedLead.lead_number}</p>
                      {selectedLead.booking_value && (
                        <p className="text-sm text-secondary-600 mt-1">
                          Booking Value: {formatIndianCurrency(selectedLead.booking_value)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>

            {/* Invoice Type */}
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Invoice Type
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {invoiceTypes.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => handleInputChange('type', type.value)}
                    className={`p-4 text-left rounded-lg border transition-colors ${
                      formData.type === type.value
                        ? 'bg-primary-50 border-primary-500 text-primary-700'
                        : 'border-secondary-300 text-secondary-600 hover:bg-secondary-50'
                    }`}
                  >
                    <p className="font-medium">{type.label}</p>
                    <p className="text-xs mt-1 opacity-75">{type.description}</p>
                  </button>
                ))}
              </div>
            </Card>

            {/* Bill To Details */}
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Bill To
              </h3>
              <div className="space-y-4">
                <Input
                  label="Name / Company Name"
                  placeholder="Enter bill to name"
                  value={formData.bill_to_name}
                  onChange={(e) => handleInputChange('bill_to_name', e.target.value)}
                />
                <Input
                  label="GSTIN (Optional)"
                  placeholder="e.g., 27AABCU9603R1ZM"
                  value={formData.bill_to_gstin || ''}
                  onChange={(e) => handleInputChange('bill_to_gstin', e.target.value)}
                  helperText="Enter GSTIN for B2B invoices"
                />
              </div>
            </Card>

            {/* Amounts */}
            <Card>
              <h3 className="text-lg font-semibold text-secondary-900 mb-4">
                Amount Details
              </h3>
              <div className="space-y-4">
                <Input
                  label="Booking Value"
                  type="number"
                  placeholder="Enter booking value"
                  value={formData.booking_value || ''}
                  onChange={(e) => handleInputChange('booking_value', Number(e.target.value))}
                  leftIcon={<span className="text-secondary-500">Rs</span>}
                />
                <Input
                  label="Brokerage Percentage"
                  type="number"
                  placeholder="Enter brokerage %"
                  value={formData.brokerage_percent || ''}
                  onChange={(e) => handleInputChange('brokerage_percent', Number(e.target.value))}
                  rightIcon={<span className="text-secondary-500">%</span>}
                  helperText="Standard brokerage is 2%"
                />

                {/* GST Toggle */}
                <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                  <div>
                    <p className="font-medium text-secondary-900">Apply GST</p>
                    <p className="text-sm text-secondary-600">
                      GST @ {formData.gst_percent}% on base amount
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowGst(!showGst)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      showGst ? 'bg-primary-600' : 'bg-secondary-300'
                    }`}
                  >
                    <div
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        showGst ? 'translate-x-6' : ''
                      }`}
                    ></div>
                  </button>
                </div>

                {/* TDS Toggle */}
                <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
                  <div>
                    <p className="font-medium text-secondary-900">Apply TDS</p>
                    <p className="text-sm text-secondary-600">
                      TDS @ {formData.tds_percent}% deducted at source
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowTds(!showTds)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      showTds ? 'bg-primary-600' : 'bg-secondary-300'
                    }`}
                  >
                    <div
                      className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        showTds ? 'translate-x-6' : ''
                      }`}
                    ></div>
                  </button>
                </div>
              </div>
            </Card>
          </div>

          {/* Sidebar - Calculator */}
          <div className="lg:col-span-1 space-y-6">
            <div className="sticky top-6">
              <InvoiceCalculator
                bookingValue={formData.booking_value || 0}
                brokeragePercent={formData.brokerage_percent || 0}
                gstPercent={formData.gst_percent || 18}
                tdsPercent={formData.tds_percent || 1}
                showGst={showGst}
                showTds={showTds}
              />

              {/* Error Message */}
              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="mt-6 space-y-3">
                <Button
                  type="submit"
                  fullWidth
                  isLoading={isSubmitting}
                  disabled={brokerageQueue.length === 0}
                >
                  Create Invoice
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  fullWidth
                  onClick={() => navigate('/billing')}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateInvoice;
